/**
 * Response Documents Component
 * 
 * Displays and manages response documents uploaded to dispute timelines.
 * 
 * Auto Starter: Read-only view, preview, download
 * Specialist Pro: Full document management (edit, replace, rotate, delete)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { metro2Service } from '../../services/metro2Service';
import { formatTimestamp, formatDate as formatDateOnly } from '../../utils/dateTime';
import {
  FileText,
  Upload,
  Download,
  Eye,
  Trash2,
  Loader2,
  File,
  Image as ImageIcon,
  Plus,
  X,
  Check,
  Paperclip,
  Edit2,
  RotateCw,
  RotateCcw,
  RefreshCw,
  Save,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Link2,
  FileType2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

// Document type colors
const DOCUMENT_TYPE_COLORS = {
  response: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  supporting: 'bg-amber-100 text-amber-700 border-amber-200',
  evidence: 'bg-amber-100 text-amber-700 border-amber-200',
};

// Source colors
const SOURCE_COLORS = {
  bureau: 'bg-slate-100 text-slate-700',
  furnisher: 'bg-emerald-100 text-emerald-700',
  consumer: 'bg-teal-100 text-teal-700',
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Check if file is an image
const isImageFile = (mimeType) => {
  return mimeType?.startsWith('image/');
};

// Get file type icon
const getFileIcon = (mimeType) => {
  if (isImageFile(mimeType)) return ImageIcon;
  if (mimeType === 'application/pdf') return FileText;
  return File;
};

// Get thumbnail URL or generate placeholder
const getThumbnailUrl = (document) => {
  if (!document.file_url) return null;
  if (isImageFile(document.file_type)) {
    // For local files, use the download endpoint
    if (document.file_url.startsWith('file://')) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/dispute-engine/documents/${document.id}/download`;
    }
    return document.file_url;
  }
  return null;
};

// ============================================
// Document Preview Modal
// ============================================
const DocumentPreviewModal = ({ 
  isOpen, 
  onClose, 
  document, 
  documents = [],
  currentIndex = 0,
  onNavigate,
}) => {
  const [rotation, setRotation] = useState(document?.rotation || 0);
  const [loading, setLoading] = useState(true);
  const isImage = isImageFile(document?.file_type);
  
  // Reset rotation when document changes
  useEffect(() => {
    setRotation(document?.rotation || 0);
    setLoading(true);
  }, [document?.id]);

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate?.(currentIndex - 1);
    if (e.key === 'ArrowRight' && currentIndex < documents.length - 1) onNavigate?.(currentIndex + 1);
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, documents.length]);

  if (!isOpen || !document) return null;

  const previewUrl = getThumbnailUrl(document) || 
    `${process.env.REACT_APP_BACKEND_URL}/api/dispute-engine/documents/${document.id}/download`;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(6,28,20,0.85)] backdrop-blur-sm"
      onClick={onClose}
      data-testid="document-preview-modal"
    >
      <div 
        className="relative max-w-5xl max-h-[90vh] w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 p-3 md:p-4 bg-gradient-to-b from-black/60 to-transparent z-10">
          <div className="flex items-center justify-between gap-2">
            <div className="text-white min-w-0 flex-1">
              <h3 className="font-medium truncate text-sm md:text-base">{document.original_filename}</h3>
              <p className="text-xs md:text-sm text-white/70">
                {formatFileSize(document.file_size)} • {formatDateOnly(document.uploaded_at)}
              </p>
            </div>
            <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
              {isImage && (
                <div className="hidden sm:flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
                    className="text-white hover:bg-white/20 h-8 w-8 p-0"
                    title="Rotate left"
                  >
                    <RotateCcw size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                    className="text-white hover:bg-white/20 h-8 w-8 p-0"
                    title="Rotate right"
                  >
                    <RotateCw size={16} />
                  </Button>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(previewUrl, '_blank')}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
                title="Open in new tab"
              >
                <Maximize2 size={16} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
              >
                <X size={18} />
              </Button>
            </div>
          </div>
        </div>

        {/* Navigation arrows */}
        {documents.length > 1 && (
          <>
            <button
              onClick={() => onNavigate?.(currentIndex - 1)}
              disabled={currentIndex === 0}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed z-10"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => onNavigate?.(currentIndex + 1)}
              disabled={currentIndex === documents.length - 1}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed z-10"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Content */}
        <div className="flex items-center justify-center h-[85vh] pt-16">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-white" />
            </div>
          )}
          
          {isImage ? (
            <img
              src={previewUrl}
              alt={document.original_filename}
              className="max-w-full max-h-full object-contain transition-transform duration-300"
              style={{ transform: `rotate(${rotation}deg)` }}
              onLoad={() => setLoading(false)}
              onError={() => setLoading(false)}
            />
          ) : document.file_type === 'application/pdf' ? (
            <iframe
              src={previewUrl}
              title={document.original_filename}
              className="w-full h-full bg-white rounded-lg"
              onLoad={() => setLoading(false)}
            />
          ) : (
            <div className="text-center text-white">
              <File className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>Preview not available for this file type</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => window.open(previewUrl, '_blank')}
              >
                <Download size={18} className="mr-2" />
                Download to view
              </Button>
            </div>
          )}
        </div>

        {/* Footer - pagination indicator */}
        {documents.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/50 text-white text-sm">
            {currentIndex + 1} / {documents.length}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// Document Edit Modal (Specialist Pro only)
// ============================================
const DocumentEditModal = ({
  isOpen,
  onClose,
  document,
  onSave,
  onReplace,
  onDelete,
}) => {
  const { user, token } = useAuth();
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [formData, setFormData] = useState({
    original_filename: '',
    description: '',
    document_type: 'response',
    source: '',
    rotation: 0,
  });

  // Initialize form when document changes
  useEffect(() => {
    if (document) {
      setFormData({
        original_filename: document.original_filename || '',
        description: document.description || '',
        document_type: document.document_type || 'response',
        source: document.source || '',
        rotation: document.rotation || 0,
      });
    }
  }, [document?.id]);

  const handleSave = async () => {
    // Validate filename is not empty
    if (!formData.original_filename?.trim()) {
      toast.error('Filename cannot be empty');
      return;
    }
    
    setSaving(true);
    try {
      await onSave(document.id, formData);
      toast.success('Document updated successfully');
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (25 MB max)
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 25 MB.');
      return;
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/tiff',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error('File type not allowed. Please upload PDF, image, or Word document.');
      return;
    }

    setReplacing(true);
    try {
      await onReplace(document.id, file);
      toast.success('File replaced successfully');
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to replace file');
    } finally {
      setReplacing(false);
    }
  };

  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(document.id);
      toast.success('Document deleted');
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to delete document');
      setDeleting(false);
    }
  };

  const handleRotate = (direction) => {
    setFormData(prev => ({
      ...prev,
      rotation: (prev.rotation + (direction === 'cw' ? 90 : -90) + 360) % 360
    }));
  };

  if (!isOpen || !document) return null;

  const isImage = isImageFile(document.file_type);
  const thumbnailUrl = getThumbnailUrl(document);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="document-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit2 size={18} />
            Edit Document
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Preview column */}
          <div className="space-y-4">
            <div 
              className="aspect-[4/3] bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden relative"
            >
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={document.original_filename}
                  className="max-w-full max-h-full object-contain transition-transform duration-300"
                  style={{ transform: `rotate(${formData.rotation}deg)` }}
                />
              ) : (
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto text-slate-400" />
                  <p className="text-sm text-slate-500 mt-2">PDF Document</p>
                </div>
              )}
            </div>

            {/* Rotation controls for images */}
            {isImage && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRotate('ccw')}
                  title="Rotate counter-clockwise"
                >
                  <RotateCcw size={16} className="mr-1" />
                  90°
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRotate('cw')}
                  title="Rotate clockwise"
                >
                  <RotateCw size={16} className="mr-1" />
                  90°
                </Button>
              </div>
            )}

            {/* Replace file button */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.tiff,.doc,.docx"
                onChange={handleFileSelect}
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={replacing}
              >
                {replacing ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <RefreshCw size={16} className="mr-2" />
                )}
                Replace File
              </Button>
            </div>
          </div>

          {/* Form column */}
          <div className="space-y-4">
            {/* Filename */}
            <div className="space-y-2">
              <Label>Filename</Label>
              <Input
                value={formData.original_filename}
                onChange={(e) => setFormData(prev => ({ ...prev, original_filename: e.target.value }))}
                placeholder="Document name"
                data-testid="edit-filename"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description..."
                rows={3}
                data-testid="edit-description"
              />
            </div>

            {/* Document Type */}
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select 
                value={formData.document_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, document_type: v }))}
              >
                <SelectTrigger data-testid="edit-document-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="response">Response Letter</SelectItem>
                  <SelectItem value="supporting">Supporting Document</SelectItem>
                  <SelectItem value="evidence">Evidence</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Source */}
            <div className="space-y-2">
              <Label>Source</Label>
              <Select 
                value={formData.source || ''} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, source: v }))}
              >
                <SelectTrigger data-testid="edit-source">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bureau">Credit Bureau</SelectItem>
                  <SelectItem value="furnisher">Data Furnisher</SelectItem>
                  <SelectItem value="consumer">Consumer/Self</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* File info */}
            <div className="pt-2 border-t">
              <p className="text-xs text-slate-500">
                <span className="font-medium">Size:</span> {formatFileSize(document.file_size)} •{' '}
                <span className="font-medium">Type:</span> {document.file_type}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                <span className="font-medium">Uploaded:</span>{' '}
                {formatTimestamp(document.uploaded_at)}
                {document.uploaded_by_name && ` by ${document.uploaded_by_name}`}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                disabled={deleting}
                data-testid="delete-document-btn"
              >
                {deleting ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <Trash2 size={16} className="mr-2" />
                )}
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Document?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete "{document.original_filename}". This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-document-btn">
              {saving ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Save size={16} className="mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// Document Card with Thumbnail
// ============================================
const DocumentCard = ({ 
  document, 
  onDownload, 
  onPreview,
  onEdit,
  onDelete, 
  canEdit,
  canDelete,
  simplified,
}) => {
  const FileIcon = getFileIcon(document.file_type);
  const thumbnailUrl = getThumbnailUrl(document);
  const isImage = isImageFile(document.file_type);
  const [imageError, setImageError] = useState(false);

  return (
    <div 
      className="group border rounded-lg bg-white hover:shadow-md transition-all overflow-hidden"
      data-testid={`document-${document.id}`}
    >
      <div className="flex">
        {/* Thumbnail section */}
        <div 
          className="w-24 h-24 flex-shrink-0 bg-slate-100 flex items-center justify-center cursor-pointer relative overflow-hidden"
          onClick={() => onPreview(document)}
        >
          {thumbnailUrl && !imageError ? (
            <>
              <img
                src={thumbnailUrl}
                alt={document.original_filename}
                className="w-full h-full object-cover"
                style={{ transform: document.rotation ? `rotate(${document.rotation}deg)` : undefined }}
                onError={() => setImageError(true)}
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <Eye size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </>
          ) : (
            <div className="text-center">
              <FileIcon size={28} className="mx-auto text-slate-400" />
              {document.file_type === 'application/pdf' && (
                <span className="text-[10px] text-slate-500 mt-1 block">PDF</span>
              )}
            </div>
          )}
        </div>

        {/* Content section */}
        <div className="flex-1 p-3 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p 
                className="font-medium text-stone-900 truncate text-sm cursor-pointer hover:text-emerald-600"
                title={document.original_filename}
                onClick={() => onPreview(document)}
              >
                {document.original_filename}
              </p>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <span className="text-xs text-slate-500">{formatFileSize(document.file_size)}</span>
                <span className="text-xs text-slate-400">•</span>
                <span className="text-xs text-slate-500">
                  {formatDateOnly(document.uploaded_at)}
                </span>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => onPreview(document)}
                title="Preview"
              >
                <Eye size={14} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => onDownload(document)}
                title="Download"
              >
                <Download size={14} />
              </Button>
              {canEdit && !simplified && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => onEdit(document)}
                  title="Edit"
                  data-testid={`edit-document-${document.id}`}
                >
                  <Edit2 size={14} />
                </Button>
              )}
            </div>
          </div>

          {/* Tags row */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {document.document_type && (
              <Badge 
                variant="outline" 
                className={`text-[10px] px-1.5 py-0 ${DOCUMENT_TYPE_COLORS[document.document_type] || ''}`}
              >
                {document.document_type === 'response' ? 'Response' : 
                 document.document_type === 'supporting' ? 'Supporting' : 'Evidence'}
              </Badge>
            )}
            {document.source && (
              <Badge 
                variant="outline" 
                className={`text-[10px] px-1.5 py-0 ${SOURCE_COLORS[document.source] || ''}`}
              >
                {document.source === 'bureau' ? 'Bureau' :
                 document.source === 'furnisher' ? 'Furnisher' : 'Self'}
              </Badge>
            )}
          </div>

          {/* Description */}
          {document.description && (
            <p className="text-xs text-slate-500 mt-2 line-clamp-1">{document.description}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Upload Form Component
// ============================================
const DocumentUploadForm = ({ 
  roundId, 
  packageId, 
  timelineId, 
  statusEventId,
  onUploadComplete 
}) => {
  const { user, token } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState('response');
  const [source, setSource] = useState('');
  const [dragActive, setDragActive] = useState(false);
  
  const validateAndSetFile = (file) => {
    const allowedTypes = [
      'application/pdf',
      'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/tiff',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('File type not allowed. Please upload PDF, image, or Word document.');
      return;
    }
    
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 25 MB.');
      return;
    }
    
    setSelectedFile(file);
  };
  
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSetFile(file);
  };
  
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('round_id', roundId);
      if (packageId) formData.append('package_id', packageId);
      if (timelineId) formData.append('timeline_id', timelineId);
      if (statusEventId) formData.append('status_event_id', statusEventId);
      formData.append('description', description);
      formData.append('document_type', documentType);
      if (source) formData.append('source', source);
      formData.append('user_id', user?.user_id || user?.id || '');
      formData.append('user_name', user?.name || user?.email || '');
      
      await metro2Service.uploadDocument(formData, token);
      
      toast.success('Document uploaded successfully');
      setSelectedFile(null);
      setDescription('');
      setDocumentType('response');
      setSource('');
      
      onUploadComplete?.();
    } catch (error) {
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <div className="space-y-4" data-testid="document-upload-form">
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 hover:border-slate-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {selectedFile ? (
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Check size={20} className="text-emerald-600" />
            </div>
            <div className="text-left">
              <p className="font-medium text-stone-900">{selectedFile.name}</p>
              <p className="text-sm text-slate-500">{formatFileSize(selectedFile.size)}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSelectedFile(null)}>
              <X size={16} />
            </Button>
          </div>
        ) : (
          <>
            <Upload size={32} className="mx-auto text-slate-400 mb-2" />
            <p className="text-slate-600 mb-2">
              Drag and drop a file here, or{' '}
              <button
                type="button"
                className="text-emerald-600 hover:text-emerald-700 font-medium"
                onClick={() => fileInputRef.current?.click()}
              >
                browse
              </button>
            </p>
            <p className="text-xs text-slate-500">PDF, images, or Word documents up to 25 MB</p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.tiff,.doc,.docx"
          onChange={(e) => e.target.files?.[0] && validateAndSetFile(e.target.files[0])}
        />
      </div>
      
      <div className="space-y-2">
        <Label>Description (Optional)</Label>
        <Textarea
          placeholder="Brief description of this document..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Document Type</Label>
          <Select value={documentType} onValueChange={setDocumentType}>
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="response">Response Letter</SelectItem>
              <SelectItem value="supporting">Supporting Document</SelectItem>
              <SelectItem value="evidence">Evidence</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Source</Label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger>
              <SelectValue placeholder="Select source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bureau">Credit Bureau</SelectItem>
              <SelectItem value="furnisher">Data Furnisher</SelectItem>
              <SelectItem value="consumer">Consumer/Self</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <Button onClick={handleUpload} disabled={!selectedFile || uploading} className="w-full">
        {uploading ? (
          <>
            <Loader2 size={18} className="animate-spin mr-2" />
            Uploading...
          </>
        ) : (
          <>
            <Upload size={18} className="mr-2" />
            Upload Document
          </>
        )}
      </Button>
    </div>
  );
};

// ============================================
// Main ResponseDocuments Component
// ============================================
export const ResponseDocuments = ({
  roundId,
  packageId,
  timelineId,
  statusEventId,
  documents: initialDocuments,
  documentsLoading: externalLoading = false,
  documentsError = null,
  deferLoad = false,
  onRequestLoad,
  simplified = false,
  onDocumentChange,
}) => {
  const { user, token } = useAuth();
  const managedByParent = Boolean(onRequestLoad);
  const [loading, setLoading] = useState(!managedByParent && !deferLoad && !initialDocuments);
  const [documents, setDocuments] = useState(initialDocuments || []);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  
  // Preview modal state
  const [previewDocument, setPreviewDocument] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  
  // Edit modal state
  const [editDocument, setEditDocument] = useState(null);
  
  useEffect(() => {
    if (initialDocuments) {
      setDocuments(initialDocuments);
    }
  }, [initialDocuments]);
  
  useEffect(() => {
    if (managedByParent || deferLoad) return;
    if ((!initialDocuments || initialDocuments.length === 0) && roundId) {
      loadDocuments();
    }
  }, [roundId, timelineId, initialDocuments, managedByParent, deferLoad]);
  
  const loadDocuments = async () => {
    if (onRequestLoad) {
      await onRequestLoad();
      return;
    }
    setLoading(true);
    try {
      const result = await metro2Service.getDocumentsForRound(roundId, token);
      setDocuments(result.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownload = (document) => {
    const url = document.file_url?.startsWith('file://') 
      ? `${process.env.REACT_APP_BACKEND_URL}/api/dispute-engine/documents/${document.id}/download`
      : document.file_url;
    
    if (url) {
      window.open(url, '_blank');
    }
  };
  
  const handlePreview = (document) => {
    const index = documents.findIndex(d => d.id === document.id);
    setPreviewIndex(index >= 0 ? index : 0);
    setPreviewDocument(document);
  };
  
  const handlePreviewNavigate = (newIndex) => {
    if (newIndex >= 0 && newIndex < documents.length) {
      setPreviewIndex(newIndex);
      setPreviewDocument(documents[newIndex]);
    }
  };
  
  const handleEdit = (document) => {
    setEditDocument(document);
  };
  
  const handleSaveEdit = async (documentId, updates) => {
    await metro2Service.updateDocumentMetadata(
      documentId,
      updates,
      user?.user_id || user?.id,
      token
    );
    loadDocuments();
    onDocumentChange?.();
  };
  
  const handleReplaceFile = async (documentId, file) => {
    await metro2Service.replaceDocumentFile(
      documentId,
      file,
      user?.user_id || user?.id,
      token
    );
    loadDocuments();
    onDocumentChange?.();
  };
  
  const handleDelete = async (documentId) => {
    await metro2Service.deleteDocument(documentId, user?.user_id || user?.id, token);
    setDocuments(prev => prev.filter(d => d.id !== documentId));
    onDocumentChange?.();
  };
  
  const handleUploadComplete = () => {
    loadDocuments();
    setShowUploadDialog(false);
    onDocumentChange?.();
  };
  
  if ((managedByParent ? externalLoading : loading)) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (documentsError) {
    return (
      <div className="text-center py-6 text-slate-500">
        <p className="text-sm">{documentsError}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={loadDocuments}>
          Retry
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="response-documents">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-stone-900 flex items-center gap-2">
          <Paperclip size={18} />
          Response Documents ({documents.length})
        </h3>
        
        {/* Upload button for Pro mode only */}
        {!simplified && (
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="upload-document-btn">
                <Plus size={16} className="mr-1" />
                Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Upload Response Document</DialogTitle>
              </DialogHeader>
              <DocumentUploadForm
                roundId={roundId}
                packageId={packageId}
                timelineId={timelineId}
                statusEventId={statusEventId}
                onUploadComplete={handleUploadComplete}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
      
      {/* Document list */}
      {documents.length === 0 ? (
        <div className="text-center py-8 bg-slate-50 rounded-lg">
          <FileText size={32} className="mx-auto text-slate-400 mb-2" />
          <p className="text-slate-500">No documents uploaded yet</p>
          {!simplified && (
            <p className="text-sm text-slate-400 mt-1">
              Upload response letters from bureaus or furnishers
            </p>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          {documents.map(doc => (
            <DocumentCard
              key={doc.id}
              document={doc}
              onDownload={handleDownload}
              onPreview={handlePreview}
              onEdit={handleEdit}
              onDelete={handleDelete}
              canEdit={!simplified}
              canDelete={!simplified && doc.uploaded_by === (user?.user_id || user?.id)}
              simplified={simplified}
            />
          ))}
        </div>
      )}
      
      {/* Preview Modal */}
      <DocumentPreviewModal
        isOpen={!!previewDocument}
        onClose={() => setPreviewDocument(null)}
        document={previewDocument}
        documents={documents}
        currentIndex={previewIndex}
        onNavigate={handlePreviewNavigate}
      />
      
      {/* Edit Modal (Pro only) */}
      {!simplified && (
        <DocumentEditModal
          isOpen={!!editDocument}
          onClose={() => setEditDocument(null)}
          document={editDocument}
          onSave={handleSaveEdit}
          onReplace={handleReplaceFile}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

// Simplified version for Auto Starter
export const SimpleResponseDocuments = ({
  roundId,
  packageId,
  timelineId,
  deferLoad = false,
  initialDocuments,
  documentsLoading = false,
  documentsError = null,
  onRequestLoad,
}) => {
  return (
    <ResponseDocuments
      roundId={roundId}
      packageId={packageId}
      timelineId={timelineId}
      documents={initialDocuments}
      documentsLoading={documentsLoading}
      documentsError={documentsError}
      deferLoad={deferLoad}
      onRequestLoad={onRequestLoad}
      simplified={true}
    />
  );
};

export default ResponseDocuments;
